/**
 * After-action report helpers (pure): which events make the timeline of big moments, and the
 * small bits of chart arithmetic the report needs (clean axis ticks, end-label placement).
 */
import { type GameEvent, NO_FACTION } from '@friction/engine';
import { getLocale, type Locale } from '../i18n';
import { type DispatchLine, describeEvent, type FormatContext } from './format';

/** Editorial weight of an event for the report's timeline; 0 = not a big moment. */
export function momentScore(ev: GameEvent, ctx: FormatContext): number {
  const { view, map } = ctx;
  const me = view.faction;
  switch (ev.kind) {
    case 'warDeclared':
      return 90;
    case 'gameOver':
      return 100;
    case 'capitulated':
      return 95;
    case 'cityCaptured': {
      if (ev.to !== me && ev.from !== me) return 0;
      const city = map.cities[ev.city];
      const capital = city && city.capitalOf !== NO_FACTION ? 30 : 0;
      return 40 + (city?.size ?? 1) * 10 + capital;
    }
    case 'encircled':
      return 55 + ev.units.length * 3;
    case 'hqLost':
      return 45;
    case 'intercept':
      return 30;
    case 'courierLost':
      return 25;
    case 'unitDestroyed':
      return 20;
    case 'decoyExposed':
      return 15;
    case 'techResearched':
      return 12;
    default:
      return 0;
  }
}

/**
 * The big moments of the war, worded like the teletype, in chronological order: the outbreak
 * and the end always, then the heaviest events up to `limit`.
 */
export function keyMoments(
  events: readonly GameEvent[],
  ctx: FormatContext,
  limit = 10,
  locale: Locale = getLocale(),
): DispatchLine[] {
  const scored = events
    .map((ev, i) => ({ ev, i, score: momentScore(ev, ctx) }))
    .filter((m) => m.score > 0)
    // Heaviest first; among equals the earlier event (it happened first, it made the news).
    .sort((a, b) => b.score - a.score || a.i - b.i)
    .slice(0, limit)
    .sort((a, b) => a.ev.tick - b.ev.tick || a.i - b.i);
  const full = { ...ctx, log: events };
  return scored.map((m) => describeEvent(m.ev, full, locale));
}

/** Clean axis ticks from 0 to at least `max` (1-2-2.5-5 steps), about `count` intervals. */
export function niceTicks(max: number, count = 4): number[] {
  if (!(max > 0)) return [0, 1];
  const raw = max / count;
  const mag = 10 ** Math.floor(Math.log10(raw));
  const step = [1, 2, 2.5, 5, 10].map((m) => m * mag).find((s) => s >= raw) ?? 10 * mag;
  const ticks: number[] = [];
  for (let v = 0; v < max + step * 0.999; v += step) ticks.push(Math.round(v * 1000) / 1000);
  return ticks;
}

/**
 * Vertical positions for end-of-line labels: each label wants to sit at its line's end (`y`),
 * but labels closer than `gap` are pushed apart (keeping order) and clamped to [top, bottom].
 * The caller draws a leader line from the line end to a label that moved.
 */
export function placeEndLabels(ys: readonly number[], gap: number, top: number, bottom: number): number[] {
  const order = ys.map((y, i) => ({ y, i })).sort((a, b) => a.y - b.y);
  const out = new Array<number>(ys.length);
  let prev = Number.NEGATIVE_INFINITY;
  for (const o of order) {
    const y = Math.max(o.y, prev + gap, top);
    out[o.i] = y;
    prev = y;
  }
  // Overflowing the bottom: shift the stack up, keeping the spacing.
  const overflow = Math.max(0, ...order.map((o) => out[o.i] - bottom));
  if (overflow > 0) {
    let next = Number.POSITIVE_INFINITY;
    for (const o of [...order].reverse()) {
      const y = Math.min(out[o.i] - overflow, next - gap);
      out[o.i] = Math.max(top, y);
      next = out[o.i];
    }
  }
  return out;
}
